import { Braces, FileCog, MousePointerClick, SlidersHorizontal, Trash2 } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import type { DocField, FieldColumns, Letterhead, SablonSurum } from '../types'
import { AddFieldGrid } from './AddFieldGrid'
import { ColumnsPicker } from './ColumnsPicker'
import { FieldPropsForm } from './FieldPropsForm'
import { LetterheadForm } from './LetterheadForm'
import { SidePanelSection } from './SidePanelSection'
import { VariableLibrary } from './VariableLibrary'

type Props = {
  field: DocField | null
  surum: SablonSurum
  onChange: (field: DocField) => void
  onInsertVariable: (key: string) => void
  onAddField: (partial: Pick<DocField, 'label' | 'type' | 'source' | 'variable' | 'key'>, columns: FieldColumns) => void
  onRemoveField?: (fieldId: string) => void
  fieldColumns?: FieldColumns
  onFieldColumnsChange?: (fieldId: string, columns: FieldColumns) => void
  onMarginsChange: (margins: SablonSurum['margins']) => void
  onLineHeightChange: (v: number) => void
  onLetterheadChange?: (letterhead: Partial<Letterhead>) => void
}

const SIDES = ['top', 'right', 'bottom', 'left'] as const
const contentClass = 'mt-0 min-h-0 flex-1 overflow-y-auto scrollbar-none overflow-x-hidden p-3 data-[state=inactive]:hidden'

export function FieldPropertiesPanel({
  field,
  surum,
  onChange,
  onInsertVariable,
  onAddField,
  onRemoveField,
  fieldColumns = 1,
  onFieldColumnsChange,
  onMarginsChange,
  onLineHeightChange,
  onLetterheadChange,
}: Props) {
  const { t } = useTranslation()
  const selectedRef = useRef<HTMLDivElement>(null)
  const fieldId = field?.id

  useEffect(() => {
    if (fieldId) selectedRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [fieldId])

  return (
    <Tabs defaultValue={field ? 'fields' : 'vars'} className="flex h-full min-w-0 flex-col">
      <div className="shrink-0 border-b px-3 py-2.5">
        <TabsList size="sm">
          <TabsTrigger value="fields">
            <SlidersHorizontal />
            {t('hr.belgeler.right.tabField')}
          </TabsTrigger>
          <TabsTrigger value="vars">
            <Braces />
            {t('hr.belgeler.right.tabVars')}
          </TabsTrigger>
          <TabsTrigger value="page">
            <FileCog />
            {t('hr.belgeler.right.tabPage')}
          </TabsTrigger>
        </TabsList>
      </div>

      <TabsContent value="fields" className={contentClass}>
        <div className="space-y-5">
          <SidePanelSection title={t('hr.belgeler.right.addField')}>
            <AddFieldGrid onAddField={onAddField} />
          </SidePanelSection>
          <div ref={selectedRef} className="scroll-mt-3 border-t pt-4">
            <SidePanelSection
              title={t('hr.belgeler.right.selectedField')}
              action={
                field && onRemoveField ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 gap-1 px-2 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
                    onClick={() => onRemoveField(field.id)}
                  >
                    <Trash2 className="size-3.5" />
                    {t('hr.belgeler.remove.confirm')}
                  </Button>
                ) : null
              }
            >
              {field ? (
                <div className="space-y-3">
                  {onFieldColumnsChange ? (
                    <div className="space-y-1.5">
                      <span className="block text-xs font-medium">{t('hr.belgeler.right.layout')}</span>
                      <ColumnsPicker value={fieldColumns} onChange={(columns) => onFieldColumnsChange(field.id, columns)} />
                    </div>
                  ) : null}
                  <FieldPropsForm field={field} onChange={onChange} />
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed px-3 py-5 text-center">
                  <span className="flex size-8 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <MousePointerClick className="size-4" />
                  </span>
                  <p className="text-xs text-muted-foreground">{t('hr.belgeler.right.noField')}</p>
                </div>
              )}
            </SidePanelSection>
          </div>
        </div>
      </TabsContent>

      <TabsContent value="vars" className={contentClass}>
        <VariableLibrary onInsertVariable={onInsertVariable} />
      </TabsContent>

      <TabsContent value="page" className={contentClass}>
        <div className="space-y-5">
          {onLetterheadChange ? <LetterheadForm surum={surum} onChange={onLetterheadChange} /> : null}
          <SidePanelSection title={t('hr.belgeler.right.pageLayout')}>
            <div className="space-y-1.5">
              <Label htmlFor="lh" className="text-xs">
                {t('hr.belgeler.page.lineHeight')}
              </Label>
              <Input
                id="lh"
                type="number"
                step="0.1"
                min={1}
                max={2.5}
                value={surum.lineHeight}
                onChange={(e) => onLineHeightChange(Number(e.target.value) || 1.5)}
              />
            </div>
          </SidePanelSection>
          <SidePanelSection title={t('hr.belgeler.right.margins')}>
            <div className="grid grid-cols-2 gap-2">
              {SIDES.map((side) => (
                <div key={side} className="min-w-0 space-y-1.5">
                  <Label htmlFor={`m-${side}`} className="block truncate text-xs">
                    {t(`hr.belgeler.page.margin.${side}`)}
                  </Label>
                  <Input
                    id={`m-${side}`}
                    type="number"
                    min={5}
                    max={40}
                    value={surum.margins[side]}
                    onChange={(e) => onMarginsChange({ ...surum.margins, [side]: Number(e.target.value) || 10 })}
                  />
                </div>
              ))}
            </div>
          </SidePanelSection>
        </div>
      </TabsContent>
    </Tabs>
  )
}
